import { ComponentProps, ReactNode, useMemo } from 'react';

import { ProfileModel } from '@pukka/api';
import { Button, ChildrenProps, SelectModal, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { useOutletSelectorState } from './OutletSelector.state';
import { OutletSelectorFooter } from './OutletSelectorFooter';
import { OutletSelectorHeader } from './OutletSelectorHeader';

interface OutletSelectorProps {
  readonly title: string;
  readonly selectedIds?: readonly number[];
  readonly disabledIds?: readonly number[];
  readonly maxItems?: number;
  readonly minItems?: number;
  readonly open?: boolean;
  readonly children?: ReactNode;
  readonly filterBy?: string;
  readonly buttonTitle?: string;
  readonly header?: ComponentProps<typeof SelectModal>['header'];
  readonly footer?: ComponentProps<typeof SelectModal>['footer'];
  readonly canSelectAll?: boolean;
  readonly errorMessage?: string | ReactNode;
  readonly itemRenderer?: (outlet: ProfileModel.Outlet) => ReactNode;
  readonly customRenderer?: (props: ChildrenProps<ProfileModel.Outlet>) => JSX.Element;
  readonly onSubmit?: (outlets: Set<ProfileModel.Outlet>) => void;
  readonly onClickCancel?: () => void;
}

function OutletSelector({
  title,
  selectedIds,
  disabledIds,
  maxItems,
  minItems,
  open,
  filterBy,
  children,
  buttonTitle,
  canSelectAll = false,
  errorMessage,
  header = OutletSelectorHeader,
  footer = OutletSelectorFooter,
  itemRenderer = (outlet) => {
    return (
      <SelectModal.Item.Default item={outlet} key={outlet.id}>
        {outlet.name}
      </SelectModal.Item.Default>
    );
  },
  customRenderer,
  onSubmit,
  onClickCancel,
}: OutletSelectorProps) {
  const state = useOutletSelectorState(buttonTitle);

  const { buttonProps, useProfile, onRetry } = state;
  const { outlets, filterOptions, status } = useSnapshot(state);
  const selectedOutlets = useMemo(() => {
    return [...outlets].filter((o) => o.id && selectedIds?.includes(o.id));
  }, [outlets, selectedIds]);
  const disabledOutlets = useMemo(() => {
    return [...outlets].filter((o) => o.id && disabledIds?.includes(o.id));
  }, [outlets, disabledIds]);

  useProfile();

  return (
    <SelectModal
      open={open}
      title={title}
      trigger={children}
      items={outlets}
      selectedItems={selectedOutlets}
      disabledItems={disabledOutlets}
      filterBy={filterBy}
      filterOptions={filterOptions}
      maxItems={maxItems}
      minItems={minItems}
      header={header}
      footer={footer}
      buttons={buttonProps}
      empty={
        status !== 'error' ? <div className="flex h-full items-center justify-center text-neutral-400">No outlets found</div> : undefined
      }
      isLoadingMoreItem={status === 'loading'}
      canSelectAll={canSelectAll}
      errorMessage={errorMessage}
      onSubmit={onSubmit}
      onClickCancel={onClickCancel}
    >
      {customRenderer ||
        (status === 'error' ? (
          <div className="flex h-full flex-col items-center justify-center text-neutral-400">
            <Text>Encountered an error while loading the data.</Text>
            <Button variant="secondary" onClick={onRetry} className="mt-3">
              Retry
            </Button>
          </div>
        ) : (
          [...outlets].map(itemRenderer)
        ))}
    </SelectModal>
  );
}

export { OutletSelector as OutletSelectorModal };
